import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IconPlus, IconLogout } from './Icons.jsx';
import { PROVIDER_PRESETS } from './providers.js';

export function Header({ user, endpoints, onCreateEndpoint, onLogout }) {
  const [createOpen, setCreateOpen] = useState(false);
  // Defensive — `user` should always be set by the parent, but if we ever
  // render without one (bad fetch, edge case), use a placeholder instead of
  // crashing on safeUser.picture / safeUser.email.
  const safeUser = user || { name: '—', email: '', picture: null };

  return (
    <header style={{
      height: 60, padding: '0 24px',
      background: '#fff', borderBottom: '1px solid var(--border-default)',
      display: 'flex', alignItems: 'center', gap: 20,
      position: 'sticky', top: 0, zIndex: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <img src="/yuno-mark.svg" alt="" style={{ width: 28, height: 28, borderRadius: 7 }} />
        <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--fg-1)', letterSpacing: '-0.01em' }}>Hooks</span>
      </div>

      <div style={{ flex: 1 }} />

      <div style={{ position: 'relative' }}>
        <button className="btn btn-primary" onClick={() => setCreateOpen(o => !o)}>
          <IconPlus size={14} /> New endpoint
        </button>
        {createOpen && (
          <NewEndpointPopover
            existing={endpoints}
            onCreate={(ep) => { onCreateEndpoint(ep); setCreateOpen(false); }}
            onClose={() => setCreateOpen(false)}
          />
        )}
      </div>

      <div style={{ width: 1, height: 24, background: 'var(--border-default)' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {safeUser.picture
          ? <img src={safeUser.picture} alt="" style={{ width: 30, height: 30, borderRadius: 999 }} />
          : <div style={{
              width: 30, height: 30, borderRadius: 999,
              background: 'var(--yuno-purple-600)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 600,
            }}>{(safeUser.name || safeUser.email || '?')[0].toUpperCase()}</div>}
        <div style={{ lineHeight: 1.2 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--fg-1)' }}>{safeUser.name || safeUser.email}</div>
          <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>{safeUser.email}</div>
        </div>
        {onLogout && (
          <button className="btn btn-ghost icon-only" onClick={onLogout} title="Sign out">
            <IconLogout size={16} />
          </button>
        )}
      </div>
    </header>
  );
}

function NewEndpointPopover({ existing, onCreate, onClose }) {
  const ref = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [onClose]);

  const existingKeys = new Set(existing.map(e => e.providerKey).filter(Boolean));
  const unused = PROVIDER_PRESETS.filter(p => !existingKeys.has(p.key));
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return unused;
    return unused.filter(p => p.name.toLowerCase().includes(q) || p.key.toLowerCase().includes(q));
  }, [query, unused]);

  const createFromPreset = (p) => onCreate({
    name: p.name, providerKey: p.key, color: p.color, glyph: p.glyph,
  });

  const createCustom = (n) => {
    const name = n.trim();
    if (!name) return;
    onCreate({
      name,
      providerKey: 'custom_' + name.toLowerCase().replace(/\s+/g, '_').slice(0, 20),
      color: 'var(--yuno-purple-600)',
      glyph: name[0].toUpperCase(),
    });
  };

  const onSubmit = () => {
    const exact = matches.find(p => p.name.toLowerCase() === query.trim().toLowerCase());
    if (exact) return createFromPreset(exact);
    if (matches.length === 1 && query.trim()) return createFromPreset(matches[0]);
    if (query.trim()) return createCustom(query);
  };

  return (
    <div ref={ref} style={{
      position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 50,
      width: 360, background: '#fff',
      border: '1px solid var(--border-default)', borderRadius: 12,
      boxShadow: 'var(--shadow-lg)', padding: 12,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: '#fff',
        border: '1px solid var(--yuno-purple-300)',
        borderRadius: 9, padding: '4px 4px 4px 10px',
        boxShadow: '0 0 0 3px var(--yuno-purple-50)',
        marginBottom: 8,
      }}>
        <IconPlus size={14} />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
          placeholder="Provider name or custom label…"
          style={{
            flex: 1, minWidth: 0, padding: '6px 0',
            border: 0, outline: 'none', fontSize: 13, fontFamily: 'inherit',
            color: 'var(--fg-1)', background: 'transparent',
          }}/>
        {query && (
          <button className="btn btn-primary btn-sm" onClick={onSubmit} style={{ padding: '4px 10px' }}>
            Create
          </button>
        )}
      </div>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--fg-4)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '6px 4px 4px' }}>
        {query.trim() ? (matches.length ? 'Matching providers' : 'No matches — press Enter to create custom') : 'Provider presets'}
      </div>
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {matches.map(p => (
          <button key={p.key} onClick={() => createFromPreset(p)} style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            padding: '7px 8px', background: 'transparent',
            border: 0, borderRadius: 7, cursor: 'pointer',
            textAlign: 'left', fontFamily: 'inherit',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--neutral-25)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <span style={{
              width: 24, height: 24, borderRadius: 6,
              background: p.color, color: '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, letterSpacing: '-0.04em', flexShrink: 0,
            }}>{p.glyph}</span>
            <span style={{ fontSize: 13, color: 'var(--fg-1)', flex: 1 }}>{p.name}</span>
          </button>
        ))}
        {query.trim() && !matches.find(p => p.name.toLowerCase() === query.trim().toLowerCase()) && (
          <>
            {matches.length > 0 && <div style={{ borderTop: '1px solid var(--border-subtle)', margin: '6px 0' }}/>}
            <button onClick={() => createCustom(query)} style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              padding: '7px 8px', background: 'transparent',
              border: 0, borderRadius: 7, cursor: 'pointer',
              textAlign: 'left', fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--neutral-25)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
              <span style={{
                width: 24, height: 24, borderRadius: 6,
                background: 'var(--yuno-purple-600)', color: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, letterSpacing: '-0.04em', flexShrink: 0,
              }}>{query.trim()[0].toUpperCase()}</span>
              <span style={{ fontSize: 13, color: 'var(--fg-1)', flex: 1 }}>
                Create <strong style={{ fontWeight: 600 }}>"{query.trim()}"</strong>
              </span>
              <span style={{ fontSize: 10.5, color: 'var(--fg-4)' }}>custom</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
